import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import type { HardwareLibrary, CreateHardwareLibrary, UpdateHardwareLibrary } from '@estimator/shared';

export function useHardwareLibrary() {
  return useQuery({
    queryKey: ['hardware-library'],
    queryFn: async () => {
      const { data } = await apiClient.get<HardwareLibrary[]>('/hardware-library');
      return data;
    },
  });
}

export function useHardwareLibraryItem(id: string | null) {
  return useQuery({
    queryKey: ['hardware-library', id],
    queryFn: async () => {
      const { data } = await apiClient.get<HardwareLibrary>(`/hardware-library/${id}`);
      return data;
    },
    enabled: !!id,
  });
}

export function useCreateHardwareLibrary() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateHardwareLibrary) => {
      const { data } = await apiClient.post<HardwareLibrary>('/hardware-library', body);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['hardware-library'] }),
  });
}

export function useUpdateHardwareLibrary() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: UpdateHardwareLibrary & { id: string }) => {
      const { data } = await apiClient.patch<HardwareLibrary>(`/hardware-library/${id}`, body);
      return data;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['hardware-library'] });
      queryClient.invalidateQueries({ queryKey: ['hardware-library', vars.id] });
    },
  });
}

export function useHardwareVendors() {
  return useQuery({
    queryKey: ['hardware-library', 'vendors'],
    queryFn: async () => {
      const { data } = await apiClient.get<string[]>('/hardware-library/vendors');
      return data;
    },
  });
}

export function useHardwareManufacturers() {
  return useQuery({
    queryKey: ['hardware-library', 'manufacturers'],
    queryFn: async () => {
      const { data } = await apiClient.get<string[]>('/hardware-library/manufacturers');
      return data;
    },
  });
}

export function useDeleteHardwareLibrary() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/hardware-library/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['hardware-library'] }),
  });
}
